import { useEffect, useRef } from 'react';

import { useLauncherCampaigns } from '../launcher-api';
import { selectGenerateSession, updateGenerateForm, useGenerateWithAIStore } from '../generate-with-ai-store';
import { CampaignCombobox } from './CampaignCombobox';

/**
 * Launcher V2 U9 (terra-l2ai-epic-076.15) — the library "Generate with AI"
 * entry's campaign picker, filling `GenerateConfigureForm`'s (U7b)
 * `campaignPicker` slot for the ONE surface that has no fixed campaign
 * already (`surface === 'library'`; the embedded surface always passes
 * `campaign` non-`null` instead, per that form's own doc). This is the only
 * genuinely new file this bead adds — `GenerateWithAIDrawer` and
 * `GenerateConfigureForm` are U7b's, reused unchanged.
 *
 * Reuses `useLauncherCampaigns()` (`../../hooks/useLauncherCampaigns.ts`) —
 * the SAME hook `PlaybooksHome.tsx` already calls, where `Campaign.id` IS
 * the SFDC campaign id (`PlaybooksHome.tsx:306` passes it straight through
 * as `campaignSfdcId`) — so this component writes `campaignSfdcId:
 * campaign.id` with no id-translation layer of its own (bead's own
 * Do-Not-Create rule).
 *
 * A plain, controlled write onto `generate-with-ai-store.ts`'s open
 * session: `onValueChange` calls `updateGenerateForm({ campaignSfdcId })`
 * and nothing else — `GenerateWithAIDrawer`'s existing "seed once per
 * campaign" effect (its own `contextQuery` + `seededForRef`) does the rest
 * (re-fetches generation-context and re-seeds `eventName`/
 * `campaignDescription`/`memberStatuses`/`instruction`) UNCHANGED; this
 * component never touches those fields itself.
 *
 * terra-qyt.15 (U10) — now a thin store-bound wrapper around the shared,
 * presentational `CampaignCombobox` (which owns the campaign list, label
 * format, the `inputValue` sync effect, and the load-error copy). This file
 * must not render the underlying Glaze combobox field primitive itself
 * directly anymore — the seed-campaign card in `QualifyStep.tsx` is the
 * second consumer of `CampaignCombobox`.
 */

export interface GenerateCampaignPickerProps {
  /**
   * The sequence's `generationPreset?.campaignSfdcId`
   * (`PlaybooksSequenceDetailResponseSchema`, U4a) — `null` until this
   * sequence has ever been generated from before. Seeded onto the open
   * session's form EXACTLY ONCE, on this component's first mount, and only
   * while the rep hasn't already picked a campaign — a fresh
   * `openGenerateSession` call always starts `campaignSfdcId: null` (that
   * store's own doc), so "already picked" here only ever means a StrictMode
   * remount of this same session, never a real prior choice this effect
   * could clobber.
   */
  presetCampaignSfdcId: string | null;
}

export function GenerateCampaignPicker({ presetCampaignSfdcId }: GenerateCampaignPickerProps) {
  const { campaigns, isLoading, isError } = useLauncherCampaigns();
  const session = useGenerateWithAIStore(selectGenerateSession);

  // StrictMode-resilient: reset at the TOP of the mount effect (root
  // CLAUDE.md "StrictMode-Resilient Effects") — a cleanup-only ref would
  // stay "already seeded" forever after the FIRST mount, breaking a real
  // remount's own preselect.
  const seededRef = useRef(false);
  useEffect(() => {
    seededRef.current = false;
  }, []);
  useEffect(() => {
    if (seededRef.current) return;
    if (presetCampaignSfdcId === null) return;
    if (session === null || session.form.campaignSfdcId !== null) return;
    seededRef.current = true;
    updateGenerateForm({ campaignSfdcId: presetCampaignSfdcId });
  }, [presetCampaignSfdcId, session]);

  if (session === null) return null;

  const selected = campaigns.find((c) => c.id === session.form.campaignSfdcId) ?? null;

  return (
    <CampaignCombobox
      campaigns={campaigns}
      value={selected}
      onChange={(campaign) => updateGenerateForm({ campaignSfdcId: campaign?.id ?? null })}
      loading={isLoading}
      isError={isError}
      ariaLabel="Choose a campaign"
      placeholder="Search campaigns…"
    />
  );
}
